import type { ReactNode } from 'react';
import { Box, VStack } from '@terra/ui-styled-system';
import { BrowserConsole } from '../components/BrowserConsole';
import { BrowserNetwork } from '../components/BrowserNetwork';
import { BrowserPageSource } from '../components/BrowserPageSource';
import { BrowserRecordingPlayer } from '../components/BrowserRecordingPlayer';
import { BrowserToolCallScreenshot } from '../components/BrowserToolCallScreenshot';
import { BrowserRenderer } from '../components/BrowserRenderer';
import { LoadToolsResultCard } from '../components/LoadToolsResultCard';
import { MarkdownPreview } from '../../../markdown-preview';
import { MetaDefault, MonoSmall } from '../../../../components/typography';
import {
  prepareToolResultData,
  ToolResultRenderer,
} from '../../components/ToolCallsRenderers';
import { formatToolOutput } from './tool-formatters';
import {
  browserNavigateOutputWithoutUrlEcho,
  cleanBrowserToolOutput,
  readBrowserConsole,
  readBrowserNetwork,
  readBrowserPageSource,
  readBrowserResultUrl,
} from './browser-tool-output';
import { EndpointDetailsRenderer } from '../components/EndpointDetailsRenderer';
import {
  isBrowserToolName,
  isHttpExchangeTool,
  isNeoWebSocketStreamingTool,
  normalizeToolName,
} from './tool-config';
import {
  isMcpToolCallViewerTool,
  McpToolCallOutputBody,
} from '../components/McpToolCallOutputBody';
import { HttpExecutionCard } from '../../../artifacts/http/HttpExecutionCard';
import {
  formatAuthenticationDetailsSummary,
  parseAuthenticationDetailsStreamOutput,
} from '@terra/shared-dto-chat-types';
import { formatHttpExchangePayloads } from './http-exchange-output.utils';
import { renderInternalNetworkToolOutputBody } from '../../components/tools/internal-network/ad-tool-output-body';
import {
  isInternalNetworkAdToolName,
  isInternalNetworkRemoteC2ToolName,
} from '../../components/tools/internal-network/internal-network-tool-names';
import { isMcpToolName } from '@terra/shared-chat-trace/trace-view/utils/tool-config.helpers';
import { WebSocketClientRenderer } from '../../components/tools/websocket-client/WebSocketClientRenderer';

import { AuthenticateRenderer } from '../../components/tools/authenticate/authenticate-result-renderer';

type RenderToolOutputParams = {
  toolName: string;
  output: string;
  args?: Record<string, unknown>;
  error?: string;
  toolCallId?: string;
};

function getNavigateWebMarkdown(output: string): string {
  try {
    const parsed: unknown = JSON.parse(output);

    if (typeof parsed !== 'object' || parsed === null) return output;

    const record = parsed as Record<string, unknown>;

    if (typeof record.result !== 'string') return output;

    const trimmed = record.result.trim();

    return trimmed.length > 0 ? trimmed : output;
  } catch {
    return output;
  }
}

function isBrowserTool(toolName: string): boolean {
  return (
    toolName?.includes('browser') ||
    toolName?.includes('mcp_cursor-ide-browser') ||
    toolName === 'navigate_web'
  );
}

function getHttpExchangeCardTitle(normalizedName: string): string {
  if (normalizedName.includes('sse')) return 'SSE';
  if (normalizedName.includes('websocket')) return 'WebSocket';

  return 'HTTP';
}

function tryParseBrowserOutput(output: string): Record<string, unknown> | null {
  try {
    const parsed = JSON.parse(output);

    if (typeof parsed !== 'object' || parsed === null) {
      return null;
    }

    const record = parsed as Record<string, unknown>;

    if (
      typeof record.result !== 'undefined' ||
      typeof record.logs !== 'undefined' ||
      typeof record.success !== 'undefined' ||
      typeof record.status !== 'undefined'
    ) {
      return record;
    }
  } catch {
    // Not JSON or not browser format
  }

  return null;
}

function isBrowserOutput(data: unknown): boolean {
  if (typeof data !== 'object' || data === null) {
    return false;
  }

  const record = data as Record<string, unknown>;

  return (
    typeof record.result !== 'undefined' ||
    typeof record.logs !== 'undefined' ||
    typeof record.success !== 'undefined' ||
    typeof record.status !== 'undefined'
  );
}

export function renderToolOutput({
  toolName,
  output,
  args,
  error,
  toolCallId,
}: RenderToolOutputParams): ReactNode {
  const normalizedName = normalizeToolName(toolName);

  if (
    normalizedName === 'browser-get-content' &&
    readBrowserPageSource(output).html
  ) {
    return <BrowserPageSource output={output} />;
  }

  if (normalizedName === 'browser-network-requests' && !error) {
    const networkResult = readBrowserNetwork(output);

    if (networkResult) {
      return (
        <BrowserNetwork
          url={networkResult.url}
          requests={networkResult.requests}
        />
      );
    }
  }

  if (normalizedName === 'browser-console-messages' && !error) {
    const consoleResult = readBrowserConsole(output);

    if (consoleResult) {
      return (
        <BrowserConsole
          url={consoleResult.url}
          messages={consoleResult.messages}
        />
      );
    }
  }

  if (
    normalizedName === 'browser-navigate' &&
    !error &&
    readBrowserResultUrl(output)
  ) {
    const requestedUrl = typeof args?.url === 'string' ? args.url : undefined;
    const extra = browserNavigateOutputWithoutUrlEcho({
      output,
      requestedUrl,
    });

    if (!extra) {
      return null;
    }

    const isLandedUrl = /^https?:\/\/\S+$/i.test(extra);

    return (
      <VStack alignItems="flex-start" gap={2} width="100%">
        {isLandedUrl ? (
          <MonoSmall
            color="fg.default"
            whiteSpace="pre-wrap"
            wordBreak="break-word"
          >
            {extra}
          </MonoSmall>
        ) : (
          <MetaDefault
            color="fg.default"
            whiteSpace="pre-wrap"
            wordBreak="break-word"
          >
            {extra}
          </MetaDefault>
        )}
        {toolCallId ? <BrowserRecordingPlayer toolCallId={toolCallId} /> : null}
        {toolCallId ? (
          <BrowserToolCallScreenshot toolCallId={toolCallId} />
        ) : null}
      </VStack>
    );
  }

  const screenshotResultUrl =
    normalizedName === 'browser-take-screenshot' && toolCallId && !error
      ? readBrowserResultUrl(output)
      : undefined;

  if (screenshotResultUrl && toolCallId) {
    return (
      <VStack alignItems="flex-start" gap={2} width="100%">
        <MetaDefault color="fg.muted">{screenshotResultUrl}</MetaDefault>
        <BrowserToolCallScreenshot
          toolCallId={toolCallId}
          showLabel={false}
          watch
        />
      </VStack>
    );
  }

  if (normalizedName === 'load-tools') {
    return <LoadToolsResultCard output={output} />;
  }

  if (normalizedName === 'authenticate') {
    return (
      <AuthenticateRenderer data={output} error={error} maxHeight="300px" />
    );
  }

  const isBrowserSessionTool = isBrowserToolName(toolName);
  const browserSessionMedia =
    isBrowserSessionTool && toolCallId ? (
      <>
        <BrowserToolCallScreenshot toolCallId={toolCallId} />
        <BrowserRecordingPlayer toolCallId={toolCallId} />
      </>
    ) : null;

  if (normalizedName === 'navigate-web') {
    const markdown = getNavigateWebMarkdown(output);

    return (
      <Box width="100%" color="fg.muted">
        <MarkdownPreview source={markdown} fontSize={13} />
        {toolCallId ? <BrowserRecordingPlayer toolCallId={toolCallId} /> : null}
      </Box>
    );
  }

  if (isNeoWebSocketStreamingTool(toolName)) {
    return <WebSocketClientRenderer data={output} input={args} error={error} />;
  }

  if (isMcpToolCallViewerTool(toolName)) {
    return (
      <McpToolCallOutputBody
        toolName={toolName}
        args={args}
        output={output}
        error={error}
      />
    );
  }

  if (isHttpExchangeTool(toolName)) {
    const {
      requestPayload,
      responsePayload,
      error: probeError,
    } = formatHttpExchangePayloads(args, output);

    return (
      <HttpExecutionCard
        title={getHttpExchangeCardTitle(normalizedName)}
        requestPayload={requestPayload}
        responsePayload={responsePayload}
        error={probeError}
      />
    );
  }

  if (normalizedName === 'ask-codebase') {
    return (
      <Box width="100%" color="fg.muted">
        <MarkdownPreview source={output} fontSize={13} />
      </Box>
    );
  }

  // Handle get-endpoint-details output
  if (normalizedName === 'get-endpoint-details') {
    try {
      const parsed = JSON.parse(output);

      if (typeof parsed === 'object' && parsed !== null) {
        return <EndpointDetailsRenderer data={parsed} />;
      }
    } catch {
      // Fall through to default rendering
    }
  }

  if (normalizedName === 'get-authentication-details') {
    const parsed = parseAuthenticationDetailsStreamOutput(output);

    if (parsed) {
      return (
        <Box width="100%" color="fg.muted" fontSize="13px">
          {formatAuthenticationDetailsSummary(parsed)}
        </Box>
      );
    }
  }

  if (isBrowserTool(toolName)) {
    if (typeof output === 'string') {
      const parsed = tryParseBrowserOutput(output);

      if (parsed && isBrowserOutput(parsed)) {
        return browserSessionMedia ? (
          <Box width="100%">
            <BrowserRenderer data={parsed} maxHeight="300px" />
            {browserSessionMedia}
          </Box>
        ) : (
          <BrowserRenderer data={parsed} maxHeight="300px" />
        );
      }
    }
  }

  if (
    isInternalNetworkAdToolName(toolName) ||
    isInternalNetworkRemoteC2ToolName(toolName) ||
    isMcpToolName(toolName) ||
    normalizedName === 'create-internal-network-discovery-signal' ||
    normalizedName === 'get-existing-internal-network-discovery-signals' ||
    normalizedName === 'create-internal-network-scenario-draft'
  ) {
    const internalNetworkBody = renderInternalNetworkToolOutputBody({
      toolName,
      output,
    });

    if (internalNetworkBody) {
      return internalNetworkBody;
    }
  }

  try {
    const toolResultData = prepareToolResultData({
      content: output,
      toolName,
    });

    if (
      toolResultData.contentType !== 'plain-text' &&
      toolResultData.contentType !== 'markdown'
    ) {
      return <ToolResultRenderer data={toolResultData} maxHeight="300px" />;
    }
  } catch (error) {
    console.warn('Error rendering tool output:', error);
  }

  if (browserSessionMedia) {
    return (
      <Box width="100%" color="fg.muted">
        <MarkdownPreview
          source={cleanBrowserToolOutput(formatToolOutput(toolName, output))}
          fontSize={12}
          compact
        />
        {browserSessionMedia}
      </Box>
    );
  }

  return (
    <MarkdownPreview
      source={
        isBrowserSessionTool
          ? cleanBrowserToolOutput(formatToolOutput(toolName, output))
          : formatToolOutput(toolName, output)
      }
      fontSize={12}
      compact
    />
  );
}
